import React, { useRef, useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CtaButton } from "@/components/ui/CtaButton";
import { ImageUp, Sparkles, X, FileText, Play, Timer as TimerIcon } from "lucide-react";
import MobiHint from "@/components/MobiHint";
import GenerateLoader from "@/components/ui/GenerateLoader";
import StoryQuizPlayer from "@/components/quiz/StoryQuizPlayer";

const TYPES = [
  { value: "mcq", label: "Multiple choice" },
  { value: "true_false", label: "True / False" },
  { value: "fill_blank", label: "Fill in the blanks" },
  { value: "short", label: "Short answer" },
];

const TIMER_OPTIONS = [
  { value: 0,   label: "No timer" },
  { value: 30,  label: "30 s" },
  { value: 45,  label: "45 s" },
  { value: 60,  label: "60 s" },
  { value: 90,  label: "90 s" },
  { value: 120, label: "2 min" },
];

export default function ScreenshotQuiz() {
  const [imageB64, setImageB64] = useState("");
  const [imageMime, setImageMime] = useState("image/png");
  const [preview, setPreview] = useState("");
  const [count, setCount] = useState(8);
  const [selected, setSelected] = useState(["mcq", "true_false", "fill_blank", "short"]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [timePerQ, setTimePerQ] = useState(60);
  const [playing, setPlaying] = useState(false);
  const fileRef = useRef(null);
  const submitBtnRef = useRef(null);
  const toggle = (v) => setSelected((s) => s.includes(v) ? s.filter((x) => x !== v) : [...s, v]);

  // A1 — once the image is captured/loaded, auto-scroll the Process button into view
  useEffect(() => {
    if (imageB64 && submitBtnRef.current) {
      const t = setTimeout(() => submitBtnRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 200);
      return () => clearTimeout(t);
    }
  }, [imageB64]);

  const readFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Image files only");
    setImageMime(f.type);
    const r = new FileReader();
    r.onload = () => {
      const dataUrl = String(r.result || "");
      setPreview(dataUrl);
      setImageB64(dataUrl.split(",")[1] || "");
      toast.success("Image captured — ready to process");
    };
    r.readAsDataURL(f);
  };

  const onFile = (e) => readFile(e.target.files?.[0]);

  // A1 — enable direct paste (Cmd+V) of an image anywhere on this page
  useEffect(() => {
    const onPaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const it of items) {
        if (it.kind === "file" && it.type.startsWith("image/")) {
          const f = it.getAsFile();
          if (f) { readFile(f); e.preventDefault(); return; }
        }
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, []);

  // Build a quiz-shaped object from the result so StoryQuizPlayer can play it
  const playableQuiz = result && {
    id: `sq-${Date.now()}`,
    title: result.title || "Screenshot Quiz",
    questions: (result.questions || []).map((q) => ({
      question: q.q,
      options: q.options || undefined,
      correct_answer: Array.isArray(q.answer) ? q.answer.join(", ") : q.answer,
      explanation: q.explanation,
    })),
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!imageB64) return toast.error("Upload an image first");
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/ai/quiz/from-image", { image_b64: imageB64, mime: imageMime, count: Number(count), types: selected });
      setResult(data.result);
      toast.success("Quiz generated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="screenshot-quiz-page">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-1">/ screenshot → quiz</div>
        <h1 className="font-display font-bold text-3xl tracking-tighter inline-flex items-center gap-2">
          <FileText size={26} /> Upload anything → instant quiz
        </h1>
        <p className="text-sm text-neutral-600 mt-1">Book pages, worksheets, homework, question papers — the AI reads and quizzes you. Tip: press <kbd className="border border-black/15 rounded px-1.5 py-0.5 text-[10px] font-mono">⌘ V</kbd> to paste directly. 7 credits.</p>
      </div>

      <form onSubmit={submit} className="bg-white border border-black/8 rounded-2xl p-6 space-y-4">
        <div className="border border-dashed border-black/15 rounded-xl p-4 bg-[#FBFAF7]">
          {preview ? (
            <div className="relative inline-block">
              <img src={preview} alt="upload" className="max-h-72 border border-black/10 rounded" />
              <button type="button" onClick={() => { setImageB64(""); setPreview(""); setResult(null); if (fileRef.current) fileRef.current.value = ""; }} className="absolute -top-2 -right-2 bg-black text-white rounded-full p-1" data-testid="sq-clear"><X size={12} /></button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center py-10 cursor-pointer text-neutral-500 hover:text-[#0A0C10]" data-testid="sq-upload-zone">
              <ImageUp size={32} />
              <span className="mt-2 text-sm font-medium">Click to upload or paste an image</span>
              <span className="text-xs text-neutral-400 mt-1">JPG, PNG, HEIC — works best with clear text</span>
              <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="sq-image-input" />
            </label>
          )}
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">Number of questions</div>
            <input type="number" min={3} max={20} value={count} onChange={(e) => setCount(e.target.value)} className="w-full bg-white border border-black/15 rounded-md px-3 py-2 text-sm" data-testid="sq-count" />
          </div>
          <div>
            <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500 inline-flex items-center gap-1"><TimerIcon size={11} /> Time per question</div>
            <select value={timePerQ} onChange={(e) => setTimePerQ(Number(e.target.value))} data-testid="sq-time-per-question" className="w-full bg-white border border-black/15 rounded-md px-3 py-2 text-sm">
              {TIMER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">Question types</div>
            <div className="flex flex-wrap gap-1.5">
              {TYPES.map((t) => (
                <button key={t.value} type="button" onClick={() => toggle(t.value)} data-testid={`sq-type-${t.value}`} className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${selected.includes(t.value) ? "bg-[#0A0C10] text-[#FBFAF7] border-[#0A0C10]" : "border-black/15 text-neutral-600 hover:border-[#0A0C10]"}`}>{t.label}</button>
              ))}
            </div>
          </div>
        </div>

        {/* A1 — sticky Process CTA. When an image is loaded, the button sticks near the bottom of the viewport so it's always reachable. */}
        <div
          ref={submitBtnRef}
          className={imageB64 ? "sticky bottom-4 z-10 -mx-6 px-6 pb-2 pt-3 bg-gradient-to-t from-white via-white/95 to-white/70 backdrop-blur-sm" : ""}
        >
          <CtaButton
            type="submit"
            variant="primary"
            size="lg"
            disabled={loading || !imageB64}
            data-testid="sq-submit"
            className="w-full rounded-md shadow-[0_10px_24px_-10px_rgba(10,12,16,0.4)]"
          >
            {loading
              ? <GenerateLoader label="Reading image" phrases={["Reading image…", "Recognising text…", "Writing questions…", "Adding explanations…"]} />
              : <><Sparkles size={16} /> {imageB64 ? "Process image → generate quiz" : "Upload an image first"}</>}
          </CtaButton>
        </div>
      </form>

      {result && (
        <div className="bg-white border border-black/8 rounded-2xl p-6 space-y-4" data-testid="sq-result">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">/ result</div>
              <h2 className="font-display font-bold text-xl">{result.title || "Screenshot quiz"}</h2>
            </div>
            <button
              onClick={() => setPlaying(true)}
              data-testid="sq-play-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#FFD600] text-[#0A0C10] text-sm font-bold hover:scale-[1.02] transition-transform"
            >
              <Play size={14} /> Play in story mode
            </button>
          </div>

          {result.transcript && (
            <details className="text-xs text-neutral-600">
              <summary className="cursor-pointer font-semibold uppercase tracking-[0.18em] text-neutral-500">What the AI read</summary>
              <div className="mt-2 whitespace-pre-wrap bg-[#FBFAF7] border border-black/8 rounded p-3 max-h-40 overflow-y-auto">{result.transcript}</div>
            </details>
          )}

          {(result.questions || []).map((q, i) => (
            <div key={i} className="border-t border-neutral-100 pt-3">
              <div className="font-medium text-sm">Q{i + 1}. {q.q} <span className="text-[10px] uppercase ml-1 text-neutral-400">{q.type}</span></div>
              {q.options && <ol className="mt-1 ml-4 text-sm space-y-0.5 list-decimal">{q.options.map((o, j) => <li key={j} className={o === q.answer ? "text-green-700 font-medium" : ""}>{o}</li>)}</ol>}
              {!q.options && <div className="text-xs text-neutral-500 mt-1"><strong>Answer:</strong> {q.answer}</div>}
              {q.explanation && <div className="text-xs text-neutral-500 mt-1 italic">{q.explanation}</div>}
            </div>
          ))}

          {(result.flashcards || []).length > 0 && (
            <div className="border-t border-neutral-100 pt-3">
              <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-2">Flashcards</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {result.flashcards.map((f, i) => (
                  <div key={i} className="bg-[#FBFAF7] border border-black/8 rounded p-3 text-xs">
                    <div className="font-medium">{f.front}</div>
                    <div className="text-neutral-500 mt-1">{f.back}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {result.revision_notes && (
            <div className="border-t border-neutral-100 pt-3">
              <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-2">Revision notes</div>
              <div className="text-sm whitespace-pre-wrap">{result.revision_notes}</div>
            </div>
          )}
        </div>
      )}

      <StoryQuizPlayer
        open={playing}
        onClose={() => setPlaying(false)}
        quiz={playableQuiz}
        timePerQ={timePerQ}
      />
      <MobiHint label="Ask Mobi about the screenshot" prompt="What's the best way to quiz students on the material in this image?" bottom={imageB64 ? "bottom-24" : "bottom-5"} />
    </div>
  );
}
